/* eslint-disable max-lines */
import { createSelector } from "@reduxjs/toolkit";
import * as httpStatus from "http-status-codes";
import { mergeWith } from "immutable";
import { cloneDeep, isEqual, merge } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useRequest } from "redux-query-react";
import { useCurrentWidgetState, usePatchedWidgetComponentProps, useResolvedCustomDateRangeEndDate, useResolvedCustomDateRangeStartDate, useResolvedTenor, useSelectedValues, useWidgetCurrentDisplayValueType, useWidgetCurrentEndDate, useWidgetCurrentExcludeSoldAssets, useWidgetCurrentGranularity, useWidgetCurrentGridSwitch, useWidgetCurrentHasDatePicker, useWidgetCurrentPeriodOfAnalysis, useWidgetCurrentReferenceDate, useWidgetCurrentStartDate } from "../../components/widgets/hooks/use-current-widget";
import { dashboardTypeToEntityType } from "../shared/dashboardTypeToEntityType";
import { buildComparisonPipeline, buildPipeline } from "./datapoints-api/pipeline";
import { AllocationKey, DatapointFilterOperatorEnum, DatapointFormatEnum, DatapointIdStandardEnum, DatapointsComparisonResponseFromJSON, DatapointsEntityType, DatapointsResponseFromJSON, DatapointTimeseriesEnum, postDatapoints, postDatapointsComparison, TenorCustomTenorPeriodEnum, UnitOfArea, useGetDatapointsMetadata } from "api/ux/src";
import { CommitmentCalculation } from "api/ux/src/models/CommitmentCalculation";
import { comparisonAliases, preview } from "common/constants";
import { dateFromNullable, getUtcStartOfDayOrUndefined } from "common/date-utils";
import { objectToHash } from "common/object-helper";
import { useGetUnitOfAreaKey, useGetUserAllocationKey, useGetUserCommitmentCalculation } from "components/frame/hooks/use-user-preferences";
import { PortfolioCreationChoice } from "components/modal/portfolio-creation-edit/types";
import { DashboardTypes } from "components/workspaces/hooks/types";
import { useCurrentDashboardAnalysis, useCurrentDashboardEntityId, useCurrentDashboardTemplate, useCurrentDashboardType, useGetSelectedCurrency, useGetSelectedDashboardOptions, useGetSelectedReferenceDate } from "components/workspaces/hooks/use-current-dashboard";
import { useFilterValues, useWidgetFilterValues } from "components/workspaces/hooks/use-get-filter-values";
import { useWorkshopFilters } from "store/widget-workshop/selectors";
import { pflAssociationSelector, usePortfolioAssociation } from "components/workspaces/hooks/use-portfolio-comparison-associations";
import { useGetEsgSustainability } from "hooks/use-tenant-settings";
import { DataGranularity, DisplayValueType, TenorOptions, WidgetMode } from "store/current-dashboard/types";
import { datapointsKey } from "store/middleware/analytics/analytics-grouping-middleware";
import { RelativeValueOptions } from "store/widget-workshop/types";
const mapMeasure = (measures) => measures?.map((row) => ({ ...row })) || [];
export const getCustomDimensionAllowedFormats = () => ["string", "boolean", "date"];
export const getCustomFieldAllowedFormats = () => ["string", "boolean", "date", "double", "percent", "value", "amount", "multiple"];
export const isCustom = (dpm, entityType, allowedFormats) => {
    if (!entityType) {
        return false;
    }
    const metadata = dpm?.metadata;
    if (!metadata?.path) {
        return false;
    }
    return metadata.isCustom?.toString() === "true" &&
        allowedFormats.some((x) => x === metadata.format?.toString()) &&
        metadata.path?.some((p) => entityType.some((ent) => ent === p));
};
export const useGetCustomDatapointDimensions = (entityType) => {
    const [customFields] = useGetDatapointsMetadata();
    if (entityType && customFields && customFields.length > 0) {
        return customFields.filter((c) => isCustom(c, entityType, getCustomDimensionAllowedFormats())) || [];
    }
    return [];
};
export const useGetCustomDatapointFields = (entityType) => {
    const [customFields] = useGetDatapointsMetadata();
    if (entityType && customFields && customFields.length > 0) {
        return customFields.filter((c) => isCustom(c, entityType, getCustomFieldAllowedFormats())) || [];
    }
    return [];
};
export const useGetCustomDatapointDimensionsByDashboardType = (dashboardType) => {
    const assetCustomFields = useGetCustomDatapointDimensions([DatapointsEntityType.Asset]), fundCustomFields = useGetCustomDatapointDimensions([DatapointsEntityType.Fund]), investorAccountCustomFields = useGetCustomDatapointDimensions(["Investor Account"]), managerCustomFields = useGetCustomDatapointDimensions([DatapointsEntityType.Manager]);
    let ret = [];
    switch (dashboardType) {
        case DashboardTypes.Portfolio: {
            ret = ret.concat(fundCustomFields, assetCustomFields, managerCustomFields, investorAccountCustomFields);
            break;
        }
        case DashboardTypes.Manager: {
            ret = ret.concat(fundCustomFields, assetCustomFields);
            break;
        }
        case DashboardTypes.Fund: {
            ret = ret.concat(assetCustomFields);
            break;
        }
        default:
            return ret;
    }
    return ret;
};
export const useGetCustomDatapointDimensionsByPortfolioCreationChoice = (portfolioCreationChoice) => {
    const fundCustomFields = useGetCustomDatapointFields([DatapointsEntityType.Fund]), investorAccountCustomFields = useGetCustomDatapointFields(["Investor Account"]);
    let ret = [];
    switch (portfolioCreationChoice) {
        case PortfolioCreationChoice.CreatePortfolioRuleBased:
        case PortfolioCreationChoice.CreatePortfolioManualCompositionInvestorAccounts: {
            ret = ret.concat(fundCustomFields, investorAccountCustomFields);
            break;
        }
        case PortfolioCreationChoice.CreatePortfolioManualCompositionFunds:
        case PortfolioCreationChoice.CreatePortfolioRuleBasedFunds: {
            ret = ret.concat(fundCustomFields);
            break;
        }
        default:
            return ret;
    }
    return ret;
};
export const useGetDatapointsWidgetParams = (props, componentprops) => {
    const { widgetId, sectionId } = props, datapoints = [], gridSwitch = useWidgetCurrentGridSwitch(sectionId, widgetId), selectedValues = useSelectedValues(sectionId, widgetId);
    let params = componentprops.datapointParams;
    const switcher = componentprops?.gridSwitcher;
    if (gridSwitch && switcher) {
        params = switcher.datapointParams;
    }
    params?.forEach((datap, index) => {
        datapoints.push({
            dimensions: (datap?.dimensions),
            id: `${props.sectionId}.${props.widgetId}.${index}`,
            measures: mapMeasure(datap.measures),
            periodOfAnalysisParams: componentprops?.periodOfAnalysisParams
        });
    });
    if (selectedValues && componentprops && componentprops.dataSelectProps) {
        componentprops.dataSelectProps.forEach((key) => {
            const { datapointParams } = selectedValues[key.order]
                ? componentprops.dataSelectProps[key.order].selectValues[selectedValues[key.order]]
                : componentprops.dataSelectProps[key.order].selectValues[Object.keys(componentprops.dataSelectProps[key.order].selectValues)[0]];
            datapointParams?.forEach((param, index) => {
                if (datapoints.length === 0) {
                    datapoints.push({
                        dimensions: param.dimensions,
                        id: `${props.sectionId}.${props.widgetId}.${index}`,
                        measures: mapMeasure(param.measures),
                        periodOfAnalysisParams: componentprops?.periodOfAnalysisParams
                    });
                }
                else {
                    for (const [key2, value] of Object.entries(param)) {
                        if (value) {
                            if (datapoints[0][key2].length === 0) {
                                datapoints[0][key2] = value;
                            }
                            else {
                                value?.forEach((singleValue) => {
                                    const existingDatapointIndex = datapoints[0][key2].findIndex((x) => x.name === singleValue?.name);
                                    if (existingDatapointIndex === -1) {
                                        datapoints[0][key2] = [...datapoints[0][key2], singleValue];
                                    }
                                    else {
                                        const clonedObject = cloneDeep(datapoints[0][key2][existingDatapointIndex]);
                                        datapoints[0][key2][existingDatapointIndex] = merge(clonedObject, singleValue);
                                    }
                                });
                            }
                        }
                    }
                }
            });
        });
    }
    return datapoints.map((x) => ({
        ...x,
        insertAdditionalColumnOnInspectData: componentprops?.insertAdditionalColumnOnInspectData,
        measures: x.measures.filter((y) => !x?.dimensions?.find((z) => z?.standardDatapoint === y.standardDatapoint && z?.customDatapoint === y?.customDatapoint))
    }));
};
const addPipelineMeasuresAndDimensionsFromDatapointParams = (componentProps, config, gridSwitch) => {
    const patchedConfig = cloneDeep(config);
    const compProps = gridSwitch ? componentProps?.gridSwitcher : componentProps;
    patchedConfig.compositeObjectBuilder = patchedConfig.compositeObjectBuilder || { dimensions: [], measures: [] };
    patchedConfig.compositeObjectBuilder.measures = patchedConfig.compositeObjectBuilder.measures || [];
    patchedConfig.compositeObjectBuilder.dimensions = patchedConfig.compositeObjectBuilder.dimensions || [];
    compProps?.datapointParams?.forEach((dpp) => {
        dpp?.measures?.forEach((d) => {
            if (d.format === "variation") {
                patchedConfig.nodesVisitorsReducer?.push({ arguments: [d.name], method: "flattenFieldsVisitor" });
            }
            patchedConfig.compositeObjectBuilder?.measures?.push(d.name || "");
        });
        dpp?.dimensions?.forEach((d) => patchedConfig.compositeObjectBuilder?.dimensions?.push(d.name || ""));
    });
    return patchedConfig;
};
const useGetDatapointsPipelineParams = (props, componentProps) => {
    const { widgetId, sectionId } = props, gridSwitch = useWidgetCurrentGridSwitch(props.sectionId, props.widgetId), selectedValues = useSelectedValues(sectionId, widgetId);
    const widgetState = useCurrentWidgetState(sectionId, widgetId);
    const [datapointsMetadata] = useGetDatapointsMetadata();
    let output = componentProps.pipelineParameter || {};
    if (gridSwitch) {
        const newOut = componentProps?.gridSwitcher?.pipelineParameter;
        if (newOut) {
            output = newOut;
        }
    }
    if (output?.compositeObjectBuilder?.options?.getMeasuresAndDimensionsFromDatapointParams === true) {
        output = addPipelineMeasuresAndDimensionsFromDatapointParams(componentProps, output, gridSwitch);
    }
    if (selectedValues && componentProps && componentProps.dataSelectProps) {
        componentProps.dataSelectProps.forEach((key) => {
            const { pipelineParameter } = selectedValues[key.order]
                ? componentProps.dataSelectProps[key.order].selectValues[selectedValues[key.order]]
                : componentProps.dataSelectProps[key.order].selectValues[Object.keys(componentProps.dataSelectProps[key.order].selectValues)[0]];
            if (pipelineParameter) {
                const builder = mergeWith((a, b) => (Array.isArray(a) ? a.concat(b) : b), output.compositeObjectBuilder || { measures: [] }, { ...pipelineParameter.compositeObjectBuilder });
                output = { ...output, ...pipelineParameter, compositeObjectBuilder: builder, widgetState };
            }
        });
    }
    return { ...output, componentProps, datapointsMetadata, widgetState };
};
export const useGetDatapointsRequestDashboardParams = (isDatapointRequest, sectionId, widgetId) => {
    const compProps = usePatchedWidgetComponentProps(sectionId, widgetId), endDate = useWidgetCurrentEndDate(sectionId, widgetId) || compProps?.datapointParams?.[0].endDate;
    let asOfDate = useGetSelectedReferenceDate();
    const currentDashboardType = useCurrentDashboardType(), entityId = useCurrentDashboardEntityId(), options = useGetSelectedDashboardOptions(), selectedCurrency = useGetSelectedCurrency(), startDate = useWidgetCurrentStartDate(sectionId, widgetId) || compProps?.datapointParams?.[0].startDate, tenor = useResolvedTenor(sectionId, widgetId) ?? compProps?.tenor;
    const { filterCashflows } = compProps;
    if (endDate !== undefined) {
        asOfDate = endDate;
    }
    return {
        asOfDate,
        entityId,
        entityType: isDatapointRequest ? dashboardTypeToEntityType(currentDashboardType) : DatapointsEntityType.Asset,
        filterCashflows,
        selectedCurrency,
        startDate: getUtcStartOfDayOrUndefined(startDate),
        tenor,
        ...options
    };
};
export const allocationKeyEnumValues = (allocationKey) => {
    const allocationKeyEnum = allocationKey?.toUpperCase();
    if (allocationKeyEnum === "NAV") {
        return AllocationKey.Nav;
    }
    if (allocationKeyEnum === "COMMITMENT") {
        return AllocationKey.Commitment;
    }
    if (allocationKeyEnum === "HYBRID") {
        return AllocationKey.Hybrid;
    }
    if (allocationKeyEnum === "GROSSNAV") {
        return AllocationKey.GrossNav;
    }
    if (allocationKeyEnum === "GROSSNAVHYBRID") {
        return AllocationKey.GrossNavHybrid;
    }
    throw new Error(`Allocation key ${allocationKey} not supported`);
};
export const commitmentCalculationEnumValues = (commitmentCalculation) => {
    if (commitmentCalculation === "historicalCommitment") {
        return CommitmentCalculation.HistoricalCommitment;
    }
    if (commitmentCalculation === "recomputedCommitment") {
        return CommitmentCalculation.RecomputedCommitment;
    }
    throw new Error(`Commitment Calculation ${commitmentCalculation} not supported`);
};
export const unitOfAreaKeyEnumValue = (unitOfArea) => {
    if (unitOfArea === "squareMeter") {
        return UnitOfArea.SquareMeter;
    }
    if (unitOfArea === "squareFoot") {
        return UnitOfArea.SquareFoot;
    }
    if (unitOfArea === "acre") {
        return UnitOfArea.Acre;
    }
    if (unitOfArea === "hectare") {
        return UnitOfArea.Hectare;
    }
    throw new Error(`Unit of Area key ${unitOfArea} not supported`);
};
export const getTimeSeries = (byGranularity) => {
    if (!byGranularity) {
        return byGranularity;
    }
    let result = null;
    if (byGranularity === DataGranularity.yearly) {
        result = DatapointTimeseriesEnum.EndOfYear;
    }
    else if (byGranularity === DataGranularity.yearOverYear) {
        result = DatapointTimeseriesEnum.YearOverYear;
    }
    else {
        result = DatapointTimeseriesEnum.EndOfQuarter;
    }
    return result;
};
export const getFormat = (format) => {
    switch (format) {
        case DisplayValueType.Percent:
            return DatapointFormatEnum.Percent;
        default:
            return null;
    }
};
export const getDatapointId = (dre) => {
    const ret = {};
    if (dre.customDatapoint) {
        ret.custom = dre.customDatapoint;
    }
    else if (dre.standardDatapoint) {
        ret.standard = dre.standardDatapoint;
    }
    return ret;
};
const standardDatapointIds = new Set(Object.values(DatapointIdStandardEnum).map((e) => e.toString()));
export const getDatapointIdFromIdString = (id) => {
    if (standardDatapointIds.has(id)) {
        return { standard: id };
    }
    return { custom: id };
};
export const hasDatapointsWidgetParams = (requestParams) => Boolean(requestParams?.length);
const setPerformancePeriod = (periodOfAnalysisParams, periodOfAnalysis, datapointRequest) => {
    if (periodOfAnalysisParams?.includes(datapointRequest?.name)) {
        return periodOfAnalysis ?? datapointRequest.performancePeriod;
    }
    return undefined;
};
export const mergeGlobalAndWidgetFilters = (globalFilters, widgetFilters) => {
    const filterMap = new Map(globalFilters);
    if (widgetFilters !== undefined && Object.keys(widgetFilters).length > 0) {
        Object.entries(widgetFilters).forEach(([key, value]) => {
            const globalValue = filterMap.get(key);
            if (Array.isArray(globalValue) && Array.isArray(value)) {
                filterMap.set(key, value.filter((v) => globalValue.includes(v)));
            }
            else {
                filterMap.set(key, value);
            }
        });
    }
    return [...filterMap.entries()];
};
const useMergedFilters = (sectionId, widgetId) => {
    const globalFilters = Object.entries(useFilterValues() || {});
    const widgetFilters = useWidgetFilterValues(sectionId, widgetId);
    const workshopFilters = useWorkshopFilters();
    const isPreviewWidget = sectionId === preview.sectionId && widgetId === preview.widgetId;
    if (isPreviewWidget) {
        return Object.entries(workshopFilters || {});
    }
    return mergeGlobalAndWidgetFilters(globalFilters, widgetFilters);
};
export const useGetCreateDatapointsRequestDependencies = (requestParams) => {
    const allocationKey = useGetUserAllocationKey(), cffModelId = useSelector((state) => state?.currentDashboard?.cffChangeModal?.selectedModelId, isEqual), commitmentCalculation = useGetUserCommitmentCalculation(), esgSustainability = useGetEsgSustainability(), [metadata] = useGetDatapointsMetadata(), isDatapointRequest = hasDatapointsWidgetParams(requestParams);
    const unitOfArea = useGetUnitOfAreaKey();
    const [sectionId, widgetId] = requestParams?.length > 0 ? requestParams[0]?.id?.split(".") || [] : [], excludeSoldAssets = useWidgetCurrentExcludeSoldAssets(sectionId, widgetId);
    const { asOfDate, startDate, entityId, entityType, selectedCurrency, filterCashflows, tenor, ...dashboardOptions } = { ...useGetDatapointsRequestDashboardParams(isDatapointRequest, sectionId, widgetId), allocationKey };
    const filters = useMergedFilters(sectionId, widgetId);
    const widgetDataFormat = useWidgetCurrentDisplayValueType(sectionId, widgetId);
    // Only update format when sectionId or widgetId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const format = useMemo(() => widgetDataFormat, [sectionId, widgetId]);
    const timePeriod = useWidgetCurrentGranularity(sectionId, widgetId), widgetCurrentReferenceDate = useWidgetCurrentReferenceDate(sectionId, widgetId), widgetCurrentReferenceDateOverride = useWidgetCurrentHasDatePicker(sectionId, widgetId) ? widgetCurrentReferenceDate : undefined;
    const widgetCurrentStatusPeriodOfAnalysis = useWidgetCurrentPeriodOfAnalysis(sectionId, widgetId);
    const customDateRangeEndDate = useResolvedCustomDateRangeEndDate(sectionId, widgetId);
    const customDateRangeStartDate = useResolvedCustomDateRangeStartDate(sectionId, widgetId);
    return {
        allocationKey,
        asOfDate,
        cffModelId,
        commitmentCalculation,
        customDateRangeEndDate,
        customDateRangeStartDate,
        dashboardOptions,
        entityId,
        entityType,
        esgSustainability,
        excludeSoldAssets,
        filterCashflows,
        filters,
        format,
        isDatapointRequest,
        metadata,
        selectedCurrency,
        startDate,
        tenor,
        timePeriod,
        unitOfArea,
        widgetCurrentReferenceDateOverride,
        widgetCurrentStatusPeriodOfAnalysis
    };
};
export const createId = (request) => {
    if (request) {
        request.id = objectToHash(request);
        request.requests?.forEach((req) => {
            req.id = `${req.id}.${request?.id}`;
        });
    }
};
const getMeasureFormat = (measure, timePeriod, format) => {
    // Format only taken into account when timeseries or if the column is relative so it needs to have Percent format at the moment
    if (measure?.relativeValueOption === RelativeValueOptions.AgainstGroups) {
        return DatapointFormatEnum.Percent;
    }
    if (measure?.relativeValueOption === RelativeValueOptions.AgainstGrandTotal) {
        return DatapointFormatEnum.PercentOfTotal;
    }
    return timePeriod && measure.format !== DatapointFormatEnum.Variation ? getFormat(format) || measure.format : measure.format;
};
const createFilter = (id, values) => {
    if (Array.isArray(values)) {
        return {
            datapoint: getDatapointIdFromIdString(id),
            operator: DatapointFilterOperatorEnum.In,
            values: values
        };
    }
    if (values.type === "date") {
        return {
            datapoint: getDatapointIdFromIdString(id),
            operator: DatapointFilterOperatorEnum.Between,
            values: [dateFromNullable(values.startDate), dateFromNullable(values.endDate)]
        };
    }
    if (values.type === "number-range") {
        return {
            datapoint: getDatapointIdFromIdString(id),
            maxNumberValue: values.upperLimit,
            minNumberValue: values.lowerLimit,
            operator: DatapointFilterOperatorEnum.BetweenNumbers
        };
    }
    throw new Error(`Filter type ${values} not supported`);
};
export const createTenor = (tenor, startDate, endDate, referenceDate) => {
    switch (tenor) {
        case TenorOptions.customDateRange:
            if (startDate && endDate) {
                return { customDateRange: { endDate, startDate } };
            }
            return undefined;
        case TenorOptions.fiveYears:
            return { custom: { length: 5, tenorPeriod: TenorCustomTenorPeriodEnum.Year } };
        case TenorOptions.Itd:
            return { sinceInception: true };
        case TenorOptions.oneMonth:
            return { custom: { length: 1, tenorPeriod: TenorCustomTenorPeriodEnum.Month } };
        case TenorOptions.oneQuarter:
            return { custom: { length: 1, tenorPeriod: TenorCustomTenorPeriodEnum.Quarter } };
        case TenorOptions.oneYear:
            return { custom: { length: 1, tenorPeriod: TenorCustomTenorPeriodEnum.Year } };
        case TenorOptions.sixMonths:
            return { custom: { length: 6, tenorPeriod: TenorCustomTenorPeriodEnum.Month } };
        case TenorOptions.tenYears:
            return { custom: { length: 10, tenorPeriod: TenorCustomTenorPeriodEnum.Year } };
        case TenorOptions.threeYears:
            return { custom: { length: 3, tenorPeriod: TenorCustomTenorPeriodEnum.Year } };
        case TenorOptions.twoYears:
            return { custom: { length: 2, tenorPeriod: TenorCustomTenorPeriodEnum.Year } };
        case TenorOptions.Ytd: {
            const baseDate = referenceDate ?? new Date();
            return { customDateRange: { endDate: baseDate, startDate: new Date(baseDate.getFullYear(), 0, 1) } };
        }
        default: return undefined;
    }
};
const isTenorMeasure = (m) => m.standardDatapoint === DatapointIdStandardEnum.FundTWR || m.standardDatapoint === DatapointIdStandardEnum.FundNetIRR;
const buildMeasureContext = (m, cffModelId, tenor, customDateRangeStartDate, customDateRangeEndDate, referenceDate) => {
    const ctx = m.context;
    return {
        ...m.context,
        cffModelId: ctx?.cffModelId === "__CFF_MODEL_ID__" ? cffModelId : ctx?.cffModelId,
        tenor: tenor && isTenorMeasure(m) ? createTenor(tenor, customDateRangeStartDate, customDateRangeEndDate, referenceDate) : undefined
    };
};
const buildDatapointRequest = (requestParam, opts) => ({
    dimensions: requestParam.dimensions?.map((m) => ({
        datapoint: getDatapointId(m),
        name: m.name
    })),
    id: requestParam.id || "",
    measures: requestParam.measures.map((m) => ({
        benchmark: m.benchmark,
        context: buildMeasureContext(m, opts.cffModelId, opts.tenor, opts.customDateRangeStartDate, opts.customDateRangeEndDate, opts.asOfDate),
        datapoint: getDatapointId(m),
        format: getMeasureFormat(m, opts.timePeriod, opts.format),
        name: m.name,
        performancePeriod: setPerformancePeriod(requestParam.periodOfAnalysisParams, opts.widgetCurrentStatusPeriodOfAnalysis, m) || undefined,
        periods: m.periods,
        projected: m.projected,
        timeseries: getTimeSeries(opts.timePeriod) || m.timeSeries
    }))
});
export const getCreateDatapointsRequest = (requestParams, requestParameterConfig, noHash) => {
    const { allocationKey, asOfDate, cffModelId, commitmentCalculation, customDateRangeEndDate, customDateRangeStartDate, dashboardOptions, entityId, entityType, esgSustainability, excludeSoldAssets, filterCashflows, filters, format, isDatapointRequest, selectedCurrency, startDate, tenor, timePeriod, unitOfArea, widgetCurrentReferenceDateOverride, widgetCurrentStatusPeriodOfAnalysis } = requestParameterConfig;
    if (!isDatapointRequest) {
        return {};
    }
    /*
     * If a merged filter has been narrowed to an empty array (e.g. widget-level
     * and dashboard-level selections on the same key are disjoint) the result
     * set is logically empty. Skip firing the request entirely; the backend
     * would otherwise treat `IN []` as no constraint and return everything.
     */
    const hasEmptyArrayFilter = (filters || []).some((v) => Array.isArray(v?.[1]) && v[1].length === 0);
    if (hasEmptyArrayFilter) {
        return {};
    }
    const requestParameters = {
        datapointsRequest: {
            allocationKey: allocationKeyEnumValues(allocationKey),
            asOfDate: dateFromNullable(widgetCurrentReferenceDateOverride || asOfDate),
            commitmentCalculation: commitmentCalculationEnumValues(commitmentCalculation),
            dashboardOptions: dashboardOptions,
            entityId,
            entityType,
            esgSustainability,
            excludeSoldAssets,
            filterCashflows,
            filters: (filters || []).map((v) => createFilter(v[0], v[1])),
            requests: requestParams.map((requestParam) => buildDatapointRequest(requestParam, {
                asOfDate: dateFromNullable(widgetCurrentReferenceDateOverride || asOfDate) ?? undefined,
                cffModelId,
                customDateRangeEndDate,
                customDateRangeStartDate,
                format,
                tenor,
                timePeriod,
                widgetCurrentStatusPeriodOfAnalysis
            })),
            selectedCurrency,
            startDate,
            unitOfArea: unitOfAreaKeyEnumValue(unitOfArea)
        }
    };
    if (!noHash) {
        createId(requestParameters.datapointsRequest);
    }
    return requestParameters;
};
export const useCreateDatapointsRequest = (requestParams, noHash) => {
    const requestParameterConfig = useGetCreateDatapointsRequestDependencies(requestParams);
    return getCreateDatapointsRequest(requestParams, requestParameterConfig, noHash);
};
const extractSubResponses = (body, fromJson) => {
    if (body?.responses?.length) {
        const response = fromJson(body);
        const result = response?.responses?.reduce((prev, curr) => {
            if (curr.id) {
                const idArray = curr.id.split("."), queryKey = idArray[idArray.length - 1];
                if (prev[queryKey]) {
                    prev[queryKey].responses?.push(curr);
                }
                else {
                    prev[queryKey] = { ...body, responses: [curr] };
                }
            }
            return prev;
        }, {});
        return { Datapoints: result };
    }
    return {};
};
export const globalQueryStateSelector = createSelector((state) => state.customQueries, (_, prefix) => prefix, (_, __, dataKey) => dataKey, (customQueries, prefix, dataKey) => customQueries?.[`${prefix}-${dataKey}`] ??
    Object.entries(customQueries || {}).find(([key]) => key.search(dataKey) > 0)?.[1]);
const useGetDatapointsQueryState = (dataKey, isAsync) => {
    const accepted = 202;
    const globalQueryState = useSelector((state) => globalQueryStateSelector(state, datapointsKey, dataKey));
    const asyncQueryState = useSelector((state) => state?.asyncRequests?.asyncQueryState?.[dataKey]);
    if (isAsync && globalQueryState && globalQueryState.isFinished && globalQueryState.status === accepted) {
        return asyncQueryState;
    }
    return globalQueryState;
};
const buildDatapointsRequest = (requestParameters, useComparison, dataKey) => {
    const hasRequestBody = Boolean(requestParameters?.datapointsRequest ||
        requestParameters?.datapointsComparisonRequest);
    if (!requestParameters || !hasRequestBody) {
        return null;
    }
    const options = { meta: { isAsync: useComparison }, queryKey: `${datapointsKey}-${dataKey}` };
    if (useComparison) {
        return postDatapointsComparison(requestParameters, { ...options, transform: (b) => extractSubResponses(b, DatapointsComparisonResponseFromJSON) });
    }
    return postDatapoints(requestParameters, { ...options, transform: (b) => extractSubResponses(b, DatapointsResponseFromJSON) });
};
export const useDatapoints = (requestParameters) => {
    const useComparison = Boolean(requestParameters?.datapointsComparisonRequest?.id);
    const dataKey = (useComparison
        ? requestParameters?.datapointsComparisonRequest?.id
        : requestParameters?.datapointsRequest?.id) ?? "";
    const request = buildDatapointsRequest(requestParameters, useComparison, dataKey);
    /*
     * `requestParameters` can be an empty `{}` when the caller has decided to
     * skip the API call (e.g. a merged filter narrowed the selection to an
     * empty set). We still need to return a finished query state so consuming
     * widgets don't stay stuck in a loading spinner.
     */
    const skipRequest = Boolean(requestParameters) && request === null;
    const [, ForceRefresh] = useRequest(request);
    const storedEntity = useSelector((state) => (useComparison ? state.asyncRequests.entities[dataKey] : state.entities.Datapoints[dataKey]), isEqual);
    const storedQueryState = useGetDatapointsQueryState(dataKey, useComparison);
    if (skipRequest) {
        return [
            { responses: [] },
            { isFinished: true, isPending: false, status: 200 },
            ForceRefresh
        ];
    }
    return [storedEntity, storedQueryState, ForceRefresh];
};
export const useGetEntityComparates = (fetch) => {
    const comparates = [];
    const entityType = dashboardTypeToEntityType(useCurrentDashboardType());
    const entityId = useCurrentDashboardEntityId();
    const isPortfolio = () => fetch && entityType === DatapointsEntityType.Portfolio;
    usePortfolioAssociation(isPortfolio() ? entityId : undefined);
    const pflAssociation = useSelector((state) => pflAssociationSelector(state, entityId), isEqual);
    if (isPortfolio() && pflAssociation?.comparatorPortfolioId) {
        comparates.push(pflAssociation.comparatorPortfolioId);
    }
    return comparates;
};
export const getCreateWidgetRequest = (params, comparates, config, mode) => {
    const datapointsRequest = getCreateDatapointsRequest(params, config, mode === WidgetMode.Comparison);
    if (mode === WidgetMode.Comparison) {
        if (comparates?.length && datapointsRequest?.datapointsRequest) {
            const comparisonRequest = {
                datapointsComparisonRequest: {
                    ...datapointsRequest.datapointsRequest,
                    comparates: comparates.map((id, index) => ({ alias: comparisonAliases.comparate(index), id })),
                    entityAlias: comparisonAliases.main
                }
            };
            createId(comparisonRequest.datapointsComparisonRequest);
            return comparisonRequest;
        }
        return null;
    }
    return datapointsRequest;
};
export const useCreateWidgetRequest = (params, mode) => {
    const comparates = useGetEntityComparates(mode === WidgetMode.Comparison);
    const requestParameterConfig = useGetCreateDatapointsRequestDependencies(params);
    return getCreateWidgetRequest(params, comparates, requestParameterConfig, mode);
};
const useRunPipeline = (props, compProps, request, queryState, response) => {
    const pipelineParameter = useGetDatapointsPipelineParams(props, compProps);
    const map = (selector, pipelineResult) => pipelineResult?.map((result, index) => {
        const additionalData = selector(index, result);
        if (result?.customResult?.length) {
            return { additionalData, data: result.customResult };
        }
        return { additionalData, data: result.root };
    });
    if (response?.responses && queryState.isFinished) {
        if (compProps.mode === WidgetMode.Comparison) {
            const pipeline = buildComparisonPipeline(pipelineParameter);
            const responseAfterPipeline = pipeline(response?.responses, request?.datapointsComparisonRequest);
            return map((index, result) => result?.additionalData, responseAfterPipeline) ?? [];
        }
        const pipeline = buildPipeline(pipelineParameter);
        const responseAfterPipeline = pipeline(response?.responses, request?.datapointsRequest);
        return map((index, result) => result?.root?.[index]?.additionalData, responseAfterPipeline) ?? [];
    }
    return [];
};
export const useRefreshValidWidgets = (refresh, queryState, widgetId) => {
    const errors = useSelector((state) => state?.errors?.widgetErrors, isEqual);
    if (queryState?.status === httpStatus.BAD_REQUEST && queryState?.isFinished && !Object.keys(errors).includes(widgetId)) {
        refresh();
    }
};
export const useGetDatapointsWidgetData = (props) => {
    const compProps = usePatchedWidgetComponentProps(props.sectionId, props.widgetId);
    const params = useGetDatapointsWidgetParams(props, compProps);
    const request = useCreateWidgetRequest(params, compProps.mode);
    const [responses, queryState, refresh] = useDatapoints(request);
    useRefreshValidWidgets(refresh, queryState, props.widgetId);
    const returnValues = useRunPipeline(props, compProps, request, queryState, responses);
    return [returnValues, queryState, refresh];
};
export const useCurrentDashboardAnalysisFilters = () => {
    const template = useCurrentDashboardTemplate();
    const analysis = useCurrentDashboardAnalysis();
    const analysisFilters = analysis?.filters ?? template?.filters ?? [];
    const filterBarFilters = analysis?.filterBarFilters ?? template?.filterBarFilters ?? [];
    const dashboardType = useCurrentDashboardType();
    const listOfCustomFields = useGetCustomDatapointDimensionsByDashboardType(dashboardType);
    const listOfCustomIds = listOfCustomFields?.filter((f) => f.id).map((f) => f.id ?? "") || [];
    return [...new Set(analysisFilters.concat(listOfCustomIds).concat(filterBarFilters))];
};
export const useCurrentDashboardLabelOverrides = () => {
    const template = useCurrentDashboardTemplate();
    return template?.datapointLabelOverrides || [];
};
export const useCurrentDashboardSectionOrder = () => {
    const template = useCurrentDashboardTemplate();
    return template?.sectionOrder || [];
};
export const useCurrentDashboardFilterBarFilters = () => {
    const template = useCurrentDashboardTemplate();
    const analysis = useCurrentDashboardAnalysis();
    const filterBarFilters = analysis?.filterBarFilters ?? template?.filterBarFilters ?? [];
    const labelOverrides = useCurrentDashboardLabelOverrides();
    const normalizedFilters = filterBarFilters.map((datapointId) => {
        const overrideLabel = labelOverrides.find((o) => o.datapointId === datapointId)?.label;
        return {
            datapointId,
            label: overrideLabel || ""
        };
    });
    return [normalizedFilters];
};
export const getDatapointLabelForDatapointId = (id) => {
    if (id === DatapointIdStandardEnum.FundFundId.toString()) {
        return DatapointIdStandardEnum.FundFundName;
    }
    if (id === DatapointIdStandardEnum.FundInvestorId.toString()) {
        return DatapointIdStandardEnum.FundInvestorName;
    }
    if (id === DatapointIdStandardEnum.FundManagerId.toString()) {
        return DatapointIdStandardEnum.FundManagerName;
    }
    if (id === DatapointIdStandardEnum.AssetAssetId.toString()) {
        return DatapointIdStandardEnum.AssetAssetName;
    }
    if (id === DatapointIdStandardEnum.ManagerManagerId.toString()) {
        return DatapointIdStandardEnum.ManagerManagerName;
    }
    return id;
};
